/* eslint-disable complexity */
import React from 'react';

import { Grid } from '../../../common/components/Grid/Grid';
import momencik from '../../../common/momencik';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { ExaminedFromExamPreviewLink } from '../Examined/ExaminedFromExamPreviewLink';
import {
  ArticleMode,
  CardType,
  EmailStatus,
} from '../../../common/spdCore/validation/schemas';
import { EmailStatusIcon } from '../Emails/EmailStatusIcon';
import { EmailSendLink } from '../Emails/EmailSendLink';
import { SimP1StatusEnum } from '../../../common/spdCore/validation/enums';
import { GetJudgmentExtendedDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/GetJudgmentExtendedDto';

import { mustBeSentToP1 } from './mustBeSentToP1';
import { JudgmentPreviewLink } from './JudgmentPreviewLink';
import { JudgmentsLink } from './JudgmentsLink';
import { JudgmentSignLink } from './JudgmentSignLink';
import { JudgmentXmlDownloadLink } from './JudgmentXmlDownloadLink';
import { JudgmentBinLink } from './JudgmentBinLink';
import { SimP1StatusBadge } from './SimP1StatusBadge';
import { CardWithJudgmentLink } from './CardWithJudgmentLink';
import { ExamLink } from './ExamLink';
import { JudgmentNumberCell } from './JudgmentNumberCell';

interface JudgmentsSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  enableSingleSelect?: boolean;
  selectedKeys?: string[];
  withHistory?: boolean;
  withFilterInColumns?: boolean;
}

export const judgmentForGrid = (
  judgment: GetJudgmentExtendedDto,
  setRefresh?: () => void | undefined,
) => {
  return {
    key: judgment.id,
    values: {
      'judgment.number': <JudgmentNumberCell judgment={judgment} />,
      'company.name':
        judgment?.companyName ||
        judgment?.exam?.examPackage?.company?.name ||
        '',
      'exam.number': (
        <>
          {judgment?.exam?.id && (
            <ExamLink
              examId={judgment?.exam?.id}
              articleMode={judgment?.article?.mode as unknown as ArticleMode}
            />
          )}{' '}
          {judgment?.exam?.number}
        </>
      ),
      'exam.description': judgment?.exam?.description || '',
      'judgment.nextExamDate': momencik(judgment.nextExamDate),
      'judgment.judgmentDate': momencik(judgment.judgmentDate),
      'article.description': judgment?.article?.description || ' ',
      'article.mode':
        (judgment?.article?.mode as unknown as ArticleMode) ===
        ArticleMode.Psychologist
          ? 'psycholog'
          : 'lekarz',
      'examined.surname': judgment?.exam?.examined && (
        <>
          <ExaminedFromExamPreviewLink examId={judgment?.exam?.id} />{' '}
          {judgment?.exam.examined.surname} {judgment?.exam.examined.firstname}
        </>
      ),
      'examined.pesel':
        judgment?.exam?.examined?.pesel ||
        (judgment?.exam?.examined?.iDCardType || '') +
          ' ' +
          (judgment?.exam?.examined?.iDCardNumber || '') ||
        '',
      'psychologist.name': judgment?.psychologist?.name || '',
      'healthcareFacility.displayName':
        judgment?.exam?.examPackage?.healthcareFacility?.displayName || '',
      preview: (
        <div className="d-flex flex-nowrap">
          {judgment?.exam?.id && (
            <JudgmentsLink
              examId={judgment?.exam?.id}
              articleMode={judgment?.article?.mode as unknown as ArticleMode}
              newWindow={true}
            />
          )}
          {judgment?.active && (
            <JudgmentPreviewLink
              judgmentId={judgment.id}
              engPdfTemplateType={judgment?.article?.engPdfTemplateType}
            />
          )}
          {judgment?.statusP1 ===
            (SimP1StatusEnum.AcceptedInP1 as unknown as string) && (
            <JudgmentXmlDownloadLink
              judgmentId={judgment.id}
              judgmentNumber={judgment.number}
            />
          )}
        </div>
      ),
      card: (
        <>
          {judgment?.exam?.id && judgment?.article?.register?.id && (
            <CardWithJudgmentLink
              examId={judgment?.exam?.id}
              active={judgment?.active || false}
              cardType={judgment?.article?.cardType as unknown as CardType}
              cardId={judgment?.card?.id}
            />
          )}
        </>
      ),
      emailStatus: (
        <>
          {judgment?.emailStatus !== undefined ? (
            <EmailStatusIcon
              status={judgment?.emailStatus as unknown as EmailStatus}
            />
          ) : (
            <>
              <EmailSendLink
                judgmentId={judgment.id}
                setRefresh={setRefresh}
                signStatus={judgment.signatureStatus}
              />
            </>
          )}
        </>
      ),
      sign: (
        <>
          {judgment?.active && !judgment.withoutSign && (
            <JudgmentSignLink
              judgmentId={judgment.id}
              signStatus={judgment.signatureStatus}
              sendToP1={mustBeSentToP1(judgment.article, judgment.judgmentDate)}
              statusP1={judgment.statusP1 as unknown as SimP1StatusEnum}
              cancelStatusP1={
                judgment.cancelStatusP1 as unknown as SimP1StatusEnum
              }
              psychologistId={judgment.psychologist?.uid}
            />
          )}
          {judgment?.active &&
            judgment.withoutSign &&
            judgment.statusP1 ===
              (SimP1StatusEnum.AcceptedInP1 as unknown as string) && (
              <div className="text-center">
                <SimP1StatusBadge
                  statusP1={judgment.statusP1 as unknown as SimP1StatusEnum}
                  outdated
                />
              </div>
            )}
          {!judgment?.active && judgment?.cancelStatusP1 && (
            <div className="text-center">
              <SimP1StatusBadge
                statusP1={judgment.statusP1 as unknown as SimP1StatusEnum}
                cancelStatusP1={
                  judgment.cancelStatusP1 as unknown as SimP1StatusEnum
                }
              />
            </div>
          )}
        </>
      ),
      bin: (
        <>
          {judgment?.active && (
            <JudgmentBinLink
              judgmentId={judgment.id}
              judgmentNumber={judgment.number}
              statusP1={judgment.statusP1 as unknown as SimP1StatusEnum}
              cancelStatusP1={
                judgment.cancelStatusP1 as unknown as SimP1StatusEnum
              }
              setRefresh={setRefresh}
            />
          )}
        </>
      ),
    },
  };
};

export const JudgmentsSearch: React.FC<JudgmentsSearchProps> = props => {
  const columns = [
    {
      header: 'Data orzeczenia',
      property: 'judgment.judgmentDate',
      noFilter: true,
    },
    { header: 'Numer', property: 'judgment.number' },
    { header: 'Nr badania', property: 'exam.number' },
    { header: 'Opis', property: 'exam.description' },
    { header: 'Podstawa', property: 'article.description' },
    { header: 'Badany', property: 'examined.surname' },
    { header: 'PESEL / dok.', property: 'examined.pesel' },
    { header: 'Placówka', property: 'healthcareFacility.displayName' },
    { header: 'Firma', property: 'company.name' },
    { header: 'Wystawił', property: 'article.mode', noSorting: true },
    {
      header: 'Psycholog/Lekarz',
      property: 'psychologist.name',
    },
    {
      header: 'Orzeczenie',
      property: 'preview',
      noSorting: true,
      noFilter: true,
    },
    { header: 'Karta', property: 'card', noSorting: true, noFilter: true },
    { header: '', property: 'emailStatus', noSorting: true, noFilter: true },
    { header: '', property: 'sign', noSorting: true, noFilter: true },
    { header: '', property: 'bin', noSorting: true, noFilter: true },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="judgment.judgmentDate"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      onRowClick={props.onRowClick}
      emptyGridMessage="Brak orzeczeń w systemie"
      refresh={props.refresh}
      enableSingleSelect={props.enableSingleSelect}
      selectedKeys={props.selectedKeys}
      withHistory={props.withHistory}
      withFilterInColumns={props.withFilterInColumns}
    />
  );
};
